/**
 * ResetPasswordPage — entry from the "Reset password" email link.
 *
 * Reads ?token=... from the URL, asks the user for a new password, and
 * submits to /api/auth/reset-password. On success the backend auto-creates a
 * session_token cookie, so we just refresh AuthContext and route to dashboard.
 */
import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Navbar from "@/components/Navbar";
import { API, useAuth } from "@/App";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Reset password — The Blk Community";
  }, []);

  if (!token) {
    return (
      <div className="min-h-screen bg-tbc-cream">
        <Navbar />
        <div className="max-w-md mx-auto mt-20 bg-white rounded-2xl p-8 text-center shadow-sm">
          <h1 className="font-heading text-2xl text-tbc-navy mb-2">Invalid reset link</h1>
          <p className="text-gray-500 text-sm mb-6">
            This link is missing its token. Try requesting a new one.
          </p>
          <Button onClick={() => navigate("/")} className="rounded-full bg-tbc-navy text-white">
            Back home
          </Button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/auth/reset-password`,
        { token, password },
        { withCredentials: true }
      );
      await checkAuth({ force: true });
      setDone(true);
      toast.success("Password reset — you're signed in.");
      setTimeout(() => navigate("/dashboard"), 1500);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't reset password.";
      toast.error(typeof msg === "string" ? msg : "Couldn't reset password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-tbc-cream">
      <Navbar />
      <div className="max-w-md mx-auto mt-20 bg-white rounded-2xl p-8 shadow-sm">
        {done ? (
          <div className="text-center py-6" data-testid="reset-done">
            <CheckCircle2 className="w-12 h-12 text-tbc-gold mx-auto mb-3" />
            <h1 className="font-heading text-2xl text-tbc-navy mb-1">Password reset</h1>
            <p className="text-gray-500 text-sm">Redirecting you to your dashboard…</p>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl text-tbc-navy mb-1" data-testid="reset-heading">
              Choose a new password
            </h1>
            <p className="text-sm text-gray-500 mb-6">
              At least 8 characters. We&apos;ll sign you in automatically after.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="new-password" className="text-xs uppercase tracking-wider text-gray-500">
                  New password
                </Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="new-password"
                    type={showPassword ? "text" : "password"}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-9 pr-9 h-11"
                    data-testid="reset-new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-tbc-navy"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="confirm-password" className="text-xs uppercase tracking-wider text-gray-500">
                  Confirm password
                </Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="confirm-password"
                    type={showPassword ? "text" : "password"}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="pl-9 h-11"
                    data-testid="reset-confirm-password"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full rounded-full bg-tbc-navy hover:bg-tbc-navy/90 text-white h-11"
                data-testid="reset-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
